import { registerLocaleData } from "@angular/common";
import { provideHttpClient } from "@angular/common/http";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideUiTour } from "ngx-ui-tour-primeng";
import { providePrimeNG } from "primeng/config";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { AppComponent } from "./app/app.component";
import { AppRoutes } from "./app/app.routes";
import { AppInitService } from "./app/app-init.service";
import { provideAuthConfig } from "./app/auth/auth-config.providers";
import { provideCoreServices } from "./app/core/core.providers";
import { GlobalInjectorService } from "./app/core/util/injector.service";
import { reducers } from "./app/store/reducers";
import { AyamiPreset } from "./assets/ayami-preset";
import { environment } from "./environments/environment";

registerLocaleData(localeFr, "fr");
registerLocaleData(localeEn, "en");
registerLocaleData(localeDe, "de");

// App Initialization Factory
function initializeApp(appInitService: AppInitService) {
  return (): Promise<void> => appInitService.init();
}

// Production Mode Check
if (environment.production) {
  enableProdMode();
}

// Bootstrap Application
bootstrapApplication(AppComponent, {
  providers: [
    // Routing - optimized zone for better performance
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(AppRoutes),

    // Core Services
    provideCoreServices(),

    // PrimeNG Configuration
    providePrimeNG({
      ripple: false,
      inputVariant: "filled", // outlined
      theme: {
        preset: AyamiPreset,
        options: {
          darkModeSelector: ".app-dark",
        },
      },
    }),

    // Module Imports
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      ToastModule,
      ConfirmDialogModule,
      StoreModule.forRoot(reducers, {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      }),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
      }),
    ),

    // Authentication
    provideAuthConfig(),

    // App Initialization
    AppInitService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AppInitService));
      return initializerFn();
    }),

    // Translate Service
    provideHttpClient(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: "/assets/i18n/",
        suffix: ".json",
      }),
    }),

    provideUiTour({
      enableBackdrop: true,
      backdropConfig: {
        offset: 10,
      },
    }),
  ],
})
  .then((appRef) => {
    GlobalInjectorService.setInjector(appRef.injector);
  })
  .catch((err) => console.error("Application bootstrap error:", err));
